import { toast } from '@webapp/components/Notification'
import { getMessageFromError } from '@webapp/core/utils/errors/getMessageFromError'
import { useProcurifyIntl } from '@webapp/hooks'
import { useMutateSetDefaultPOPDFLabelSet } from '@webapp/modules/purchasing/features/set-default-po-pdf-label-set/models'
import { type PDFLabelSet } from '@webapp/modules/vendor/entities/PDFLabelSet' // eslint-disable-line import/no-internal-modules
import messages from './messages'

export interface UseSetDefaultPOPDFLabelSetPresenterOptions {
  /** Receives the new current primary key: `set.key` on set, `null` on clear. */
  onSuccess?: (currentPrimaryKey: string | null) => void
}

/**
 * Toggle a set's default status (BHV-039). The direction is derived from
 * the two arguments: no current primary → set; clicking the current
 * default → clear; otherwise → change.
 */
export const useSetDefaultPOPDFLabelSetPresenter = (
  options: UseSetDefaultPOPDFLabelSetPresenterOptions = {}
) => {
  const intl = useProcurifyIntl()
  const { mutateAsync, isLoading } = useMutateSetDefaultPOPDFLabelSet()

  const toggleDefault = async (
    set: PDFLabelSet,
    currentPrimaryKey: string | null
  ) => {
    const isClearing = set.key === currentPrimaryKey

    try {
      await mutateAsync({ labelSet: set, currentPrimaryKey })

      toast.success({
        title: intl.formatMessage(messages.successTitle),
        children: intl.formatMessage(
          isClearing
            ? messages.removedAsDefaultMessage
            : messages.setAsDefaultMessage,
          { name: set.value.name }
        ),
      })

      options.onSuccess?.(isClearing ? null : set.key)
    } catch (error) {
      const errorMessage =
        getMessageFromError(error) ||
        intl.formatMessage(messages.defaultErrorMessage)

      toast.error({
        title: intl.formatMessage(messages.errorTitle),
        children: errorMessage,
      })
    }
  }

  return {
    commands: {
      toggleDefault,
    },
    states: {
      isSaving: isLoading,
    },
  }
}
